import { ProviderContextItemDto, SalesItemModel } from "@/apidtos";

export const ASSIGNED_PROVIDER_ID_TAG_KEY = "AssignedProviderId";
export const DIRECTIONS_TAG_KEY = "Directions";
export const SHIPPING_CONTACT_TAG_KEY = "shipping_contact";
export const PATIENT_TAG_KEY = "patient";

export function getTag(si: SalesItemModel, key: string): string | undefined {
  return si.Tags?.find(t => t.Key?.toLowerCase() === key.toLowerCase())?.Value ?? undefined;
}

export function hasDirections(si: SalesItemModel): boolean {
  return !!getTag(si, DIRECTIONS_TAG_KEY)?.trim();
}

export function flattenConcentration(raw?: string | null): string {
  if (!raw?.trim()) return "";
  return raw
    .split(/[|\n\r]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .join(" · ");
}

/** "200 mg/mL · 10 mL"; bare numeric volumes get an mL suffix. */
export function formatStrengthVolume(strength?: string | null, volume?: string | null): string {
  const vol = volume?.trim() ?? "";
  const volText = vol && /^[\d.]+$/.test(vol) ? `${vol} mL` : vol;
  return [flattenConcentration(strength), volText].filter(Boolean).join(" · ");
}

export function formatMoney(value?: number | null): string {
  return `$${(value ?? 0).toFixed(2)}`;
}

export function hasShippingContact(si: SalesItemModel): boolean {
  const raw = getTag(si, SHIPPING_CONTACT_TAG_KEY);
  const id = raw ? Number.parseInt(raw, 10) : 0;
  return id > 0;
}

export function isProductLine(si: SalesItemModel): boolean {
  if (si.TypeId === "Discount" || si.TypeId === "Shipping" || si.TypeId === "Tax") {
    return false;
  }
  return si.ProductId != null && si.ProductId > 0;
}

/** True when the current user is a Prescriber for the line's AssignedProviderId. */
export function canCurrentUserSignControlledLine(
  si: SalesItemModel,
  providers: ProviderContextItemDto[],
): boolean {
  if (!si.Product?.IsControlledSubstance) {
    return true;
  }
  const raw = getTag(si, ASSIGNED_PROVIDER_ID_TAG_KEY);
  const providerId = raw ? Number.parseInt(raw, 10) : 0;
  if (!providerId) {
    return false;
  }
  const link = providers.find(p => p.ProviderId === providerId);
  if (!link) {
    return false;
  }
  // Staff / RX-delegate links cannot complete DEA/EPCS.
  return link.LinkType !== "Staff";
}

/** Controlled lines the current user cannot sign (must be staged). */
export function controlledLinesNeedingStaging(
  items: SalesItemModel[],
  providers: ProviderContextItemDto[],
): SalesItemModel[] {
  return items.filter(
    i => isProductLine(i)
      && i.Product?.IsControlledSubstance
      && !canCurrentUserSignControlledLine(i, providers),
  );
}

/**
 * Controlled lines that must be staged, plus every other product line that shares
 * the same AssignedProviderId (or no provider) and shipping_contact — so shipping
 * is charged once when the Prescriber completes the order.
 */
export function linesNeedingStaging(
  items: SalesItemModel[],
  providers: ProviderContextItemDto[],
): SalesItemModel[] {
  const productItems = items.filter(isProductLine);
  const triggers = controlledLinesNeedingStaging(productItems, providers);
  if (triggers.length === 0) {
    return [];
  }

  const destinationKeys = new Set<string>();
  for (const line of triggers) {
    const providerRaw = getTag(line, ASSIGNED_PROVIDER_ID_TAG_KEY);
    const contactRaw = getTag(line, SHIPPING_CONTACT_TAG_KEY);
    const providerId = providerRaw ? Number.parseInt(providerRaw, 10) : 0;
    const contactId = contactRaw ? Number.parseInt(contactRaw, 10) : 0;
    if (providerId > 0 && contactId > 0) {
      destinationKeys.add(`${providerId}:${contactId}`);
    }
  }

  const byId = new Map<number, SalesItemModel>();
  for (const line of productItems) {
    const contactRaw = getTag(line, SHIPPING_CONTACT_TAG_KEY);
    const contactId = contactRaw ? Number.parseInt(contactRaw, 10) : 0;
    if (!contactId) continue;

    const providerRaw = getTag(line, ASSIGNED_PROVIDER_ID_TAG_KEY);
    const providerId = providerRaw ? Number.parseInt(providerRaw, 10) : 0;

    for (const key of destinationKeys) {
      const [destProvider, destContact] = key.split(":").map(Number);
      if (contactId !== destContact) continue;
      if (providerId > 0 && providerId !== destProvider) continue;
      if (line.Id != null) byId.set(line.Id, line);
      break;
    }
  }

  return Array.from(byId.values());
}
